import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { TID } from "@/lib/testIds";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  { key: "flower", label: "Flower", colour: "var(--red)" },
  { key: "pre-rolls", label: "Pre-rolls", colour: "var(--yellow)" },
  { key: "vapes", label: "Vapes", colour: "var(--blue)" },
  { key: "edibles", label: "Edibles", colour: "var(--pink)" },
  { key: "concentrates", label: "Concentrates", colour: "var(--green)" },
  { key: "topicals", label: "Tinctures", colour: "var(--blue-light)" },
  { key: "accessories", label: "Accessories", colour: "var(--text)" },
];

export default function CustomerHome() {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(null);

  useEffect(() => {
    api.get("/stores").then((r) => setStore(r.data[0] || null));
  }, []);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    api.get("/products", { params }).then((r) => setProducts(r.data));
  }, [q, category]);

  const grouped = useMemo(() => {
    const map = {};
    for (const p of products) {
      (map[p.category] = map[p.category] || []).push(p);
    }
    return map;
  }, [products]);

  const displayCategories = category ? [category] : CATEGORIES.map((c) => c.key);

  return (
    <div data-testid={TID.homeHero} className="space-y-14">
      {/* Hero — bright shop-front */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-12 gap-6"
      >
        <div className="col-span-12 lg:col-span-8 card-panel relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 sm:p-10">
              <span className="label-eyebrow">Aisle · Home</span>
              <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[0.95]">
                <span className="text-[var(--red)]">Fresh</span> from{" "}
                <span className="italic font-serif-display" style={{ fontFamily: "'Fraunces', serif" }}>
                  the shop.
                </span>
              </h1>
              <p className="mt-5 text-[var(--text-2)] max-w-md">
                {store?.tagline || "Cape Town's brightest cannabis corner shop — retro pop, delivered."}
              </p>
              <div className="mt-6 relative max-w-md">
                <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text)]" />
                <Input
                  data-testid={TID.searchInput}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search strains, brands, edibles…"
                  className="pl-11 bg-white border-2 border-[var(--text)] rounded-full h-12 text-[var(--text)] placeholder:text-[var(--text-3)]"
                  style={{ boxShadow: "3px 3px 0 0 var(--text)" }}
                />
              </div>
            </div>
            <div
              className="min-h-[240px]"
              style={{
                backgroundImage: "url('/hero-cartoon.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundColor: "#FFF3D6",
              }}
            />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 grid grid-cols-2 gap-4">
          <div className="card-panel p-5 flex flex-col justify-between" style={{ background: "var(--yellow)" }}>
            <span className="label-eyebrow" style={{ color: "var(--text)" }}>Special</span>
            <p className="font-display text-2xl leading-tight mt-2">R25 off orders over R500</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-2">Auto-applied at basket</p>
          </div>
          <div className="card-panel p-5 text-white flex flex-col justify-between" style={{ background: "var(--blue)" }}>
            <span className="label-eyebrow" style={{ color: "#fff" }}>Delivery</span>
            <p className="font-display text-2xl leading-tight mt-2">45&nbsp;min average</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-2 text-white/85">Anywhere in Cape Town</p>
          </div>
          <div className="card-panel p-5 col-span-2 bg-dots-yellow">
            <span className="label-eyebrow">Members earn</span>
            <p className="font-display text-3xl mt-2">1 stamp / R100</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-2">Fill your card, get R50 back</p>
          </div>
        </div>
      </motion.section>

      {/* Category chips */}
      <section>
        <div className="flex items-center gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          <button
            data-testid="category-all"
            onClick={() => setCategory(null)}
            className={`chip ${category === null ? "chip-active" : ""}`}
          >
            All aisles
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`category-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`chip whitespace-nowrap ${category === c.key ? "chip-active" : ""}`}
              style={category === c.key ? { background: c.colour, color: "#fff" } : {}}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Products by aisle */}
      {displayCategories.map((catKey) => {
        const list = grouped[catKey];
        if (!list || list.length === 0) return null;
        const meta = CATEGORIES.find((c) => c.key === catKey) || { label: catKey, colour: "var(--red)" };
        return (
          <section key={catKey} className="relative">
            <div className="relative mb-6">
              <div className="flex items-end justify-between">
                <div>
                  <span className="label-eyebrow" style={{ color: meta.colour }}>
                    Aisle
                  </span>
                  <h2 className="mt-1 font-display text-4xl">{meta.label}</h2>
                </div>
                <span className="text-sm font-bold text-[var(--text-2)]">{list.length} items</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {list.map((p) => (
                <ProductCard key={p.product_id} product={p} storeId={p.store_id} accent={meta.colour} />
              ))}
            </div>
          </section>
        );
      })}

      {products.length === 0 && q && (
        <div className="card-panel p-10 text-center">
          <p className="font-display text-2xl">No matches for &ldquo;{q}&rdquo;</p>
          <p className="mt-2 text-[var(--text-2)]">Try another aisle.</p>
        </div>
      )}
    </div>
  );
}
